/**
 * Security utilities for sanitizing user input and output
 * Prevents XSS attacks and ensures data safety
 * Uses DOMPurify for robust HTML sanitization
 */

import DOMPurify from 'dompurify'

/**
 * Sanitize HTML using DOMPurify - industry-standard XSS prevention
 * Removes dangerous HTML tags, scripts, and event handlers
 * Safe for displaying user-generated content
 */
export function sanitizeHTML(str: string | null | undefined): string {
  if (!str || typeof str !== 'string') return ''

  // Use DOMPurify to sanitize HTML - removes all dangerous content
  // This handles XSS attacks, malicious scripts, event handlers, etc.
  return DOMPurify.sanitize(str, {
    // Only allow safe text content, strip all HTML
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    // Return plain text, not HTML
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  }).trim()
}

/**
 * Sanitize user text input
 * Uses DOMPurify to strip HTML tags, then trims and limits length
 * Safe for user input from forms, APIs, localStorage, cookies
 */
export function sanitizeText(str: string | null | undefined, maxLength = 5000): string {
  if (!str || typeof str !== 'string') return ''

  // Use DOMPurify to remove all HTML/scripts first (more secure than regex)
  let cleaned = DOMPurify.sanitize(str, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  })

  // Trim whitespace
  cleaned = cleaned.trim()

  // Limit length
  if (maxLength && cleaned.length > maxLength) {
    cleaned = cleaned.substring(0, maxLength)
  }

  return cleaned
}

/**
 * Sanitize data from localStorage or cookies before display
 * Critical for security - localStorage can be manipulated by XSS
 * Always sanitize before displaying data from storage
 */
export function sanitizeStorageData(
  data: string | null | undefined,
  maxLength = 5000
): string {
  return sanitizeText(data, maxLength)
}

/**
 * Sanitize user object from localStorage/cookies
 * Ensures all string fields are safe for display
 * Preserves the original type structure
 */
export function sanitizeUserData<T extends Record<string, unknown> = Record<string, unknown>>(
  user: T | null | undefined
): T | null {
  if (!user || typeof user !== 'object') return user || null

  const sanitized = { ...user }
  const sanitizedRecord = sanitized as Record<string, unknown>

  // Sanitize all string fields that might be displayed
  if (typeof sanitizedRecord.name === 'string') {
    sanitizedRecord.name = sanitizeNameDisplay(sanitizedRecord.name)
  }
  
  if (typeof sanitizedRecord.username === 'string') {
    sanitizedRecord.username = sanitizeUsernameDisplay(sanitizedRecord.username)
  }
  
  if (typeof sanitizedRecord.email === 'string') {
    sanitizedRecord.email = sanitizeStorageData(sanitizedRecord.email, 255)
  }

  return sanitizedRecord as T
}

/**
 * Sanitize username for display
 * Trims and removes HTML, but preserves username format
 */
export function sanitizeUsernameDisplay(
  username: string | null | undefined
): string {
  if (!username || typeof username !== 'string') return ''
  return sanitizeText(username, 32)
}

/**
 * Sanitize user name for display
 * Trims and removes HTML
 */
export function sanitizeNameDisplay(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') return ''
  return sanitizeText(name, 100)
}

